import React, { useMemo } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { positiveNumberFieldProps } from "../../../../../utils/helpers";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../../utils/inputLimits";
import { usePortalGroups } from "../../../hooks/usePortalGroups";

const StepCircle = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      bgcolor: brand[600],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ color: "white", fontWeight: 600, fontSize: 14 }}>
      {number}
    </Typography>
  </Box>
);

type RuleDetailsErrors = {
  name?: string;
  portalGroupUid?: string;
  newStartersDays?: string;
  existingStaffDays?: string;
};

type RuleDetailsProps = {
  name: string;
  onNameChange: (v: string) => void;
  portalId: string;
  portalGroupUid: string | null | undefined;
  onPortalGroupUidChange: (uid: string | null) => void;
  newStartersDays: string;
  onNewStartersDaysChange: (v: string) => void;
  existingStaffDays: string;
  onExistingStaffDaysChange: (v: string) => void;
  errors?: RuleDetailsErrors;
};

export const RuleDetails: React.FC<RuleDetailsProps> = ({
  name,
  onNameChange,
  portalId,
  portalGroupUid,
  onPortalGroupUidChange,
  newStartersDays,
  onNewStartersDaysChange,
  existingStaffDays,
  onExistingStaffDaysChange,
  errors = {},
}) => {
  const { groups, loading: groupsLoading } = usePortalGroups(portalId, true);

  const HOMEPAGE_VALUE = "homepage";


  const dropdownValue = useMemo(() => {
    if (groupsLoading) return "";
    if (portalGroupUid === null) return HOMEPAGE_VALUE;
    if (!groups.length) return "";
    if (groups.some((g) => g.id === portalGroupUid)) return portalGroupUid;
    for (const group of groups) {
      if (group.subGroups.some((sg) => sg.id === portalGroupUid)) {
        return `${group.id}:${portalGroupUid}`;
      }
    }
    return "";
  }, [portalGroupUid, groups, groupsLoading]);

  const handleChange = (compositeValue: string) => {
    if (!compositeValue || compositeValue === HOMEPAGE_VALUE) {
      onPortalGroupUidChange(null);
      return;
    }
    const parts = compositeValue.split(":");
    onPortalGroupUidChange(parts.length === 2 ? parts[1] : parts[0]);
  };

  return (
    <Paper sx={{ p: 3, borderRadius: "12px" }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <StepCircle number={1} />
        <Typography variant="h6" fontWeight={600}>
          Rule details
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Rule name
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          <TextField
            fullWidth
            required
            size="small"
            placeholder="e.g. New starters"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            slotProps={{
              htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
            }}
            sx={{
              "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" },
            }}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Placement
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          <FormControl fullWidth size="small" error={Boolean(errors.portalGroupUid)}>
            <Select
              id="rule-placement-select"
              required
              value={dropdownValue}
              onChange={(e) => handleChange(e.target.value)}
              disabled={groupsLoading}
              displayEmpty
              sx={{ borderRadius: "8px", height: 44 }}
              startAdornment={
                groupsLoading ? (
                  <Box sx={{ display: "flex", py: 1, pr: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                ) : null
              }
              renderValue={(val) => {
                if (!val)
                  return (
                    <Box component="span" sx={{ color: gray[400] }}>
                      Select one
                    </Box>
                  );
                if (val === HOMEPAGE_VALUE) return "Homepage";
                const [groupId, subGroupId] = val.split(":");
                const group = groups.find((g) => g.id === groupId);
                if (!group)
                  return (
                    <Box component="span" sx={{ color: gray[400] }}>
                      Select one
                    </Box>
                  );
                  return subGroupId
                  ? `${group.name} > ${group.subGroups.find((sg) => sg.id === subGroupId)?.name ?? subGroupId}`
                  : group.name;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    borderRadius: "8px",
                    marginTop: "4px",
                  },
                },
              }}
            >
              <MenuItem value={HOMEPAGE_VALUE}>Homepage</MenuItem>
              {groups.flatMap((group) => [
                <MenuItem
                  key={`group-${group.id}`}
                  value={group.id}
                  sx={{
                    pl: 2,
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  {group.name}
                </MenuItem>,
                ...group.subGroups.map((subGroup) => (
                  <MenuItem
                    key={`subgroup-${subGroup.id}`}
                    value={`${group.id}:${subGroup.id}`}
                    sx={{
                      pl: 4,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    {subGroup.name}
                  </MenuItem>
                )),
              ])}
            </Select>
            {errors.portalGroupUid && (
              <FormHelperText>{errors.portalGroupUid}</FormHelperText>
            )}
          </FormControl>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            New starters deadline
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              fullWidth
              required
              size="small"
              placeholder="e.g. 30"
              type="number"
              value={newStartersDays}
              onChange={(e) =>
                onNewStartersDaysChange(e.target.value.replace(/\D/g, ""))
              }
              error={Boolean(errors.newStartersDays)}
              helperText={errors.newStartersDays}
              sx={{
                "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" },
              }}
              {...positiveNumberFieldProps}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              days
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Existing staff deadline
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              fullWidth
              required
              size="small"
              placeholder="e.g. 30"
              type="number"
              value={existingStaffDays}
              onChange={(e) =>
                onExistingStaffDaysChange(e.target.value.replace(/\D/g, ""))
              }
              error={Boolean(errors.existingStaffDays)}
              helperText={errors.existingStaffDays}
              sx={{
                "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" },
              }}
              {...positiveNumberFieldProps}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              days
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        display="flex"
        alignItems="flex-start"
        gap="4px"
        sx={{
          bgcolor: "#FFFCF5",
          border: "1px solid #FEF0C7",
          borderRadius: "12px",
          p: "8px 12px",
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 16, color: "#DC6803", mt: 0.2, flexShrink: 0 }}
        />
        <Typography variant="body2" sx={{ color: "#DC6803" }}>
          When a user matches the filters in this rule, the assignment will be
          given with the deadline and the placement above
        </Typography>
      </Box>
    </Paper>
  );
};
